import React, { FC, useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useAppSelector } from '../../../../hooks/redux';
import { FormInputText } from '../../../inputs/FormInputText';
import { FormDatePicker } from '../../../inputs/FormDatePicker';
import { CustomMUIButton } from '../../../commons/buttons/CustomMUIButton';
import { ITerminationStep, ITerminateForm } from '../../../../models/ContractTermination';
import { ButtonClassNames, InputTextFormat, LocalStorageItems, InputRegexPattern } from '../../../../models/Common';
import { FormInputCheckbox } from '../../../inputs/FormInputCheckbox';
import { ReactComponent as CheckBoxIcon } from '../../../../assets/images/EmptyCheckBox.svg';
import { DateVOv2 } from '../../../../core/DateVOv2';

interface TerminateRunProps {
  formState: ITerminateForm;
  setFormState: React.Dispatch<React.SetStateAction<ITerminateForm>>;
  changeTerminateStep: (step: ITerminationStep) => void;
}

export const TerminateRun: FC<TerminateRunProps> = ({ formState, setFormState, changeTerminateStep }) => {
  const { t } = useTranslation();
  const [isConfirmed, setIsConfirmed] = useState<boolean>(formState.isIAgree);
  const [confirmationHighlighted, setConfirmationHighlighted] = useState<boolean>(false);
  const { userInfoState } = useAppSelector((state) => state.userInfoReducer);
  const cachedData = localStorage.getItem(`${LocalStorageItems.CallMeBack}-${userInfoState.id}`);
  const showDefaults = cachedData && JSON.parse(cachedData)[userInfoState.id];
  const defaultPhone =
    showDefaults && JSON.parse(cachedData)[userInfoState.id].phone
      ? JSON.parse(cachedData)[userInfoState.id].phone
      : '';
  const minDate = new DateVOv2().getLuxonKiev().plus({ days: 1 });
  const maxDate = new DateVOv2().getLuxonKiev().plus({ days: 30 });
  const methods = useForm({
    defaultValues: {
      phone: formState.phone || userInfoState.SmsSettings?.Phone || defaultPhone,
      terminationDate: formState.terminationDate ?? minDate,
      isIAgree: formState.isIAgree,
    },
  });
  const { handleSubmit, setValue } = methods;

  useEffect(() => {
    setFormState((prevState) => {
      return {
        ...prevState,
        isDirty: methods.formState.isDirty,
      };
    });
  }, [methods.formState.isDirty]);

  useEffect(() => {
    setValue('terminationDate', formState.terminationDate ?? minDate);
  }, []);

  const handleProceed = (data: { phone: string; terminationDate: unknown }) => {
    if (!isConfirmed) {
      setConfirmationHighlighted(true);
      return;
    }
    setFormState((prevState) => {
      return {
        ...prevState,
        phone: data.phone,
        terminationDate: data.terminationDate as DateTime<boolean>,
        isIAgree: true,
      };
    });
    changeTerminateStep('refund');
  };

  const handleConfirmTermination = (checked: boolean) => {
    if (checked) setConfirmationHighlighted(false);
    setIsConfirmed(checked);
  };

  return (
    <FormProvider {...methods}>
      <form
        className={`account__terminate__run ${
          confirmationHighlighted ? 'account__terminate__run__confirmation-highlighted' : ''
        }`}
        onSubmit={handleSubmit(handleProceed)}
      >
        <div className="account__terminate__run__inputs">
          <div className="account__terminate__run__input">
            <FormInputText
              name="phone"
              label={t('input.phone')}
              className="primary"
              isRequired
              format={InputTextFormat.Phone}
              pattern={InputRegexPattern.Phone}
            />
          </div>
          <div className="account__terminate__run__input">
            <FormDatePicker
              name="terminationDate"
              label={t('terminateContract.date')}
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>
        </div>
        <div style={{ textAlign: 'left' }}>
          <FormInputCheckbox
            name="isIAgree"
            defaultChecked={formState.isIAgree}
            onChange={handleConfirmTermination}
            icon={<CheckBoxIcon />}
            label={<div className="account__terminate__run__confirmation-label">{t('terminateContract.confirm')}</div>}
          />
        </div>
        <div className="modals__btns-wrapper">
          <div className="btn-group">
            <CustomMUIButton
              label={t('buttons.cancel')}
              type="button"
              className={ButtonClassNames.PrimaryFulfilled}
              handleClick={() => changeTerminateStep(undefined)}
              isBold
            />
          </div>
          <div className="modals__btn-group">
            <CustomMUIButton
              label={t('buttons.continue')}
              type="submit"
              className={ButtonClassNames.SecondaryOutlined}
              isBold
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
